import React, { useState, useCallback, useRef } from 'react';
import { BarChart3, TrendingUp, Play, Hash } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import MapLibreEngine from '../components/map-v2/MapLibreEngine';
import MapFilterPanel from '../components/map/MapFilterPanel';
import MapLayerToggle from '../components/map/MapLayerToggle';
import ProjectDetailPanel from '../components/map/ProjectDetailPanel';
import MapStatsOverlay from '../components/map/MapStatsOverlay';
import MapAnalyticsFloating from '../components/map/MapAnalyticsFloating';
import PresentationMode from '../components/map/PresentationMode';

const INITIAL_FILTERS = {
  regionId: '',
  cityId: '',
  districtId: '',
  categoryId: '',
  subcategoryId: '',
  search: '',
};

const INITIAL_LAYERS = {
  projects: true,
  successStories: false,
  outlets: false,
  jada30: false,
};

export default function MapPage() {
  const { language, toggleLanguage } = useLanguage();
  const isArabic = language === 'ar';
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [layers, setLayers] = useState(INITIAL_LAYERS);
  const [resultCount, setResultCount] = useState(0);
  const [selectedProject, setSelectedProject] = useState(null);
  const [presentationActive, setPresentationActive] = useState(false);
  const [activePanel, setActivePanel] = useState(null);
  const [statsGeoId, setStatsGeoId] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const mapResetRef = useRef(null);
  const zoomToRegionRef = useRef(null);
  const zoomToCoordRef = useRef(null);

  const isStatsMode = activePanel === 'stats';

  const handleResetMap = useCallback(() => {
    if (mapResetRef.current) mapResetRef.current();
    setSelectedProject(null);
  }, []);

  const handleProjectSelect = useCallback((project) => {
    setSelectedProject(project);
    // Don't reset presentation mode — it manages its own lifecycle
  }, []);

  const handleZoomToRegion = useCallback((geoId) => {
    if (zoomToRegionRef.current) zoomToRegionRef.current(geoId);
  }, []);

  const startPresentation = useCallback(() => {
    setPresentationActive(true);
    setActivePanel('presentation');
  }, []);

  const stopPresentation = useCallback(() => {
    setPresentationActive(false);
    setActivePanel(null);
  }, []);

  const handleStatsRegionSelect = useCallback((geoId) => {
    if (isStatsMode && geoId) {
      setStatsGeoId(geoId);
    }
  }, [isStatsMode]);

  const handleStatsOpen = useCallback(() => {
    setActivePanel('stats');
    setStatsGeoId('');
  }, []);

  const handleStatsClose = useCallback(() => {
    setActivePanel(null);
    setStatsGeoId('');
  }, []);

  const hasFilters = filters.regionId || filters.cityId || filters.districtId
    || filters.categoryId || filters.subcategoryId || filters.search;
  const showEmpty = hasFilters && resultCount === 0;

  return (
    <MapLibreEngine
      filters={filters}
      layers={layers}
      onResultCount={setResultCount}
      onProjectSelect={handleProjectSelect}
      resetRef={mapResetRef}
      zoomToRegionRef={zoomToRegionRef}
      zoomToCoordRef={zoomToCoordRef}
      presentationMode={presentationActive}
      statsMode={isStatsMode}
      onStatsRegionSelect={handleStatsRegionSelect}
      filterOpen={filterOpen}
      projectOpen={!!selectedProject}
    >
      <MapFilterPanel
        filters={filters}
        onChange={setFilters}
        resultCount={resultCount}
        onResetMap={handleResetMap}
        onOpenChange={setFilterOpen}
      />
      <MapLayerToggle
        layers={layers}
        onChange={setLayers}
      />
      <ProjectDetailPanel
        project={selectedProject}
        onClose={() => setSelectedProject(null)}
        onViewProject={handleProjectSelect}
      />
      <MapStatsOverlay
        onZoomToRegion={handleZoomToRegion}
        isOpen={isStatsMode}
        onOpen={handleStatsOpen}
        onClose={handleStatsClose}
        hidden={!!selectedProject}
        statsGeoId={statsGeoId}
        onStatsGeoIdChange={setStatsGeoId}
      />
      <MapAnalyticsFloating
        filters={filters}
        isOpen={activePanel === 'analytics'}
        onOpen={() => setActivePanel('analytics')}
        onClose={() => setActivePanel(null)}
        hidden={!!selectedProject || isStatsMode}
      />
      <PresentationMode
        active={presentationActive}
        onToggle={stopPresentation}
        hidden={!!selectedProject}
        zoomToCoordRef={zoomToCoordRef}
        onSelectProject={handleProjectSelect}
        onReset={handleResetMap}
      />

      {/* ═══ Unified Control Panel — hidden when any mode is active ═══ */}
      {!selectedProject && !presentationActive && !isStatsMode && activePanel !== 'analytics' && (
        <div className="map-controls-panel" data-testid="map-controls-panel">
          <button
            className={`map-ctrl-btn${isStatsMode ? ' active' : ''}`}
            data-testid="ctrl-stats"
            onClick={isStatsMode ? handleStatsClose : handleStatsOpen}
          >
            <BarChart3 size={16} />
            <span>{isArabic ? 'الإحصائيات' : 'Statistics'}</span>
          </button>

          <button
            className={`map-ctrl-btn${activePanel === 'analytics' ? ' active' : ''}`}
            data-testid="ctrl-analytics"
            onClick={() => activePanel === 'analytics' ? setActivePanel(null) : setActivePanel('analytics')}
          >
            <TrendingUp size={16} />
            <span>{isArabic ? 'التحليلات' : 'Analytics'}</span>
          </button>

          <button
            className={`map-ctrl-btn${presentationActive ? ' active' : ''}`}
            data-testid="ctrl-presentation"
            onClick={startPresentation}
          >
            <Play size={16} />
            <span>{isArabic ? 'وضع العرض' : 'Presentation'}</span>
          </button>

          {resultCount > 0 && (
            <div className="map-ctrl-btn info" data-testid="ctrl-result-count">
              <Hash size={16} />
              <span>{isArabic ? `عدد المشاريع : ${resultCount}` : `Projects: ${resultCount}`}</span>
            </div>
          )}
        </div>
      )}

      {showEmpty && (
        <div className="map-empty-state" data-testid="map-empty-state">
          <span>{isArabic ? 'لا توجد نتائج' : 'No results found'}</span>
        </div>
      )}

      <button
        className="map-lang-toggle"
        data-testid="map-language-toggle"
        onClick={toggleLanguage}
        title={isArabic ? 'Switch to English' : 'التبديل للعربية'}
      >
        {isArabic ? 'EN' : 'ع'}
      </button>
    </MapLibreEngine>
  );
}
